import { useState, useEffect } from "react";
import {
  Phone, Plus, MapPin, ToggleLeft, ToggleRight, Pencil, Trash2, Search,
  CheckCircle, Star, StarOff, Signal, Globe, Clock, PhoneOutgoing, PhoneIncoming,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LocalPresenceService } from "@/services/local-presence.service";
import {
  LocalPresenceNumber, MarketMapping, CallLogEntry,
  INITIAL_NUMBER_POOL, MARKET_MAPPINGS, INITIAL_CALL_LOG,
} from "@/data/localPresence";
import { formatPhoneNumber } from "@/lib/phoneFormat";

const RESULT_STYLES: Record<string, { label: string; color: string }> = {
  connected: { label: "Connected", color: "bg-success/10 text-success border-success/20" },
  voicemail: { label: "Voicemail", color: "bg-warning/10 text-warning border-warning/20" },
  no_answer: { label: "No Answer", color: "bg-muted text-muted-foreground border-border" },
  busy: { label: "Busy", color: "bg-info/10 text-info border-info/20" },
  failed: { label: "Failed", color: "bg-destructive/10 text-destructive border-destructive/20" },
};

const MATCH_STYLES: Record<string, { label: string; color: string }> = {
  exact: { label: "Exact Match", color: "bg-success/10 text-success border-success/20" },
  market: { label: "Market Match", color: "bg-info/10 text-info border-info/20" },
  default: { label: "Default", color: "bg-muted text-muted-foreground border-border" },
};

export function LocalPresenceAdmin() {
  const [numbers, setNumbers] = useState<LocalPresenceNumber[]>(INITIAL_NUMBER_POOL);
  const [callLog] = useState<CallLogEntry[]>(INITIAL_CALL_LOG);

  useEffect(() => {
    LocalPresenceService.getAll()
      .then((rows) => { if (rows.length > 0) setNumbers(rows); })
      .catch(() => {/* table not yet created — keep seed data */});
  }, []);
  const [activeSubTab, setActiveSubTab] = useState("numbers");
  const [search, setSearch] = useState("");
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<LocalPresenceNumber | null>(null);
  const [draft, setDraft] = useState<Partial<LocalPresenceNumber>>({
    phoneNumber: "", areaCode: "", city: "", state: "FL", providerId: "", active: true, isDefault: false,
  });

  const activeCount = numbers.filter((n) => n.active).length;
  const defaultNumber = numbers.find((n) => n.isDefault);

  const filteredNumbers = numbers.filter((n) =>
    !search ||
    n.phoneNumber.includes(search) ||
    n.city.toLowerCase().includes(search.toLowerCase()) ||
    n.areaCode.includes(search)
  );

  const filteredLogs = callLog.filter((l) =>
    !search ||
    l.targetName.toLowerCase().includes(search.toLowerCase()) ||
    l.targetNumber.includes(search) ||
    l.userName.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleActive = (id: string) => {
    setNumbers((prev) => prev.map((n) => {
      if (n.id !== id) return n;
      LocalPresenceService.update(id, { active: !n.active }).catch(() => {});
      return { ...n, active: !n.active };
    }));
    toast.success("Number status updated");
  };

  const handleSetDefault = (id: string) => {
    LocalPresenceService.setDefault(id).catch(() => {});
    setNumbers((prev) => prev.map((n) => ({ ...n, isDefault: n.id === id })));
    toast.success("Default outbound number updated");
  };

  const handleDelete = (id: string) => {
    const num = numbers.find((n) => n.id === id);
    if (num?.isDefault) { toast.error("Cannot delete the default number"); return; }
    LocalPresenceService.delete(id).catch(() => {});
    setNumbers((prev) => prev.filter((n) => n.id !== id));
    toast.success("Number removed");
  };

  const openAdd = () => {
    setDraft({ phoneNumber: "", areaCode: "", city: "", state: "FL", providerId: "", active: true, isDefault: false });
    setAddDialog(true);
  };

  const openEdit = (n: LocalPresenceNumber) => {
    setDraft({ ...n });
    setEditDialog(n);
  };

  const handleSaveNew = () => {
    if (!draft.phoneNumber?.trim() || !draft.areaCode?.trim()) {
      toast.error("Phone number and area code are required");
      return;
    }
    const newNum: LocalPresenceNumber = {
      id: `lp-${Date.now()}`,
      phoneNumber: draft.phoneNumber || "",
      areaCode: draft.areaCode || "",
      city: draft.city || "",
      state: draft.state || "FL",
      active: draft.active !== false,
      providerId: draft.providerId || "",
      isDefault: false,
    };
    LocalPresenceService.create(newNum)
      .then((saved) => setNumbers((prev) => [...prev.filter(n => n.id !== newNum.id), saved]))
      .catch(() => {});
    setNumbers((prev) => [...prev, newNum]);
    setAddDialog(false);
    toast.success("Number added to pool");
  };

  const handleSaveEdit = () => {
    if (!editDialog) return;
    LocalPresenceService.update(editDialog.id, draft).catch(() => {});
    setNumbers((prev) => prev.map((n) => n.id === editDialog.id ? { ...n, ...draft } as LocalPresenceNumber : n));
    setEditDialog(null);
    toast.success("Number updated");
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Phone className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold">{numbers.length}</p>
              <p className="text-[10px] text-muted-foreground">Total Numbers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-success/10 flex items-center justify-center">
              <Signal className="h-4 w-4 text-success" />
            </div>
            <div>
              <p className="text-lg font-bold">{activeCount}</p>
              <p className="text-[10px] text-muted-foreground">Active Numbers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-warning/10 flex items-center justify-center">
              <Globe className="h-4 w-4 text-warning" />
            </div>
            <div>
              <p className="text-lg font-bold">{new Set(numbers.filter((n) => n.active).map((n) => MARKET_MAPPINGS.find((m) => m.areaCode === n.areaCode)?.market)).size}</p>
              <p className="text-[10px] text-muted-foreground">Markets Covered</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList className="h-8 bg-muted/60">
            <TabsTrigger value="numbers" className="text-[11px] gap-1"><Phone className="h-3 w-3" /> Number Pool</TabsTrigger>
            <TabsTrigger value="markets" className="text-[11px] gap-1"><MapPin className="h-3 w-3" /> Market Mapping</TabsTrigger>
            <TabsTrigger value="log" className="text-[11px] gap-1"><Clock className="h-3 w-3" /> Call Log</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="h-8 pl-7 text-xs w-48" />
            </div>
            {activeSubTab === "numbers" && (
              <Button size="sm" className="text-xs h-8 gap-1" onClick={openAdd}>
                <Plus className="h-3.5 w-3.5" /> Add Number
              </Button>
            )}
          </div>
        </div>

        {/* ── Number Pool ── */}
        <TabsContent value="numbers" className="mt-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" /> Outbound Number Pool
              </CardTitle>
              <CardDescription className="text-xs">
                Managed numbers used for local presence calling. Default: {defaultNumber?.phoneNumber || "None set"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Number</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Area Code</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">City / Market</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Provider ID</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNumbers.map((n) => {
                      const market = MARKET_MAPPINGS.find((m) => m.areaCode === n.areaCode)?.market || "—";
                      return (
                        <tr key={n.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="py-2.5 px-3 font-medium flex items-center gap-1.5">
                            {n.isDefault && <Star className="h-3 w-3 text-warning fill-warning" />}
                            {n.phoneNumber}
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground">{n.areaCode}</td>
                          <td className="py-2.5 px-3">
                            <span>{n.city}</span>
                            <span className="text-muted-foreground ml-1">· {market}</span>
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground font-mono text-[10px]">{n.providerId}</td>
                          <td className="py-2.5 px-3">
                            <Badge variant="outline" className={`text-[10px] ${n.active ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground"}`}>
                              {n.active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleSetDefault(n.id)} title="Set as default">
                                {n.isDefault ? <Star className="h-3 w-3 text-warning fill-warning" /> : <StarOff className="h-3 w-3 text-muted-foreground" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleToggleActive(n.id)} title="Toggle active">
                                {n.active ? <ToggleRight className="h-3 w-3 text-success" /> : <ToggleLeft className="h-3 w-3 text-muted-foreground" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(n)} title="Edit">
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(n.id)} title="Remove">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Market Mapping ── */}
        <TabsContent value="markets" className="mt-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> Area Code → Market Mapping
              </CardTitle>
              <CardDescription className="text-xs">
                Geographic mapping used for fallback number selection when exact area code match is unavailable
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Area Code</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">City</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">State</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Market</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Pool Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MARKET_MAPPINGS.filter((m) =>
                      !search || m.areaCode.includes(search) || m.city.toLowerCase().includes(search.toLowerCase()) || m.market.toLowerCase().includes(search.toLowerCase())
                    ).map((m) => {
                      const hasNumber = numbers.some((n) => n.active && n.areaCode === m.areaCode);
                      return (
                        <tr key={m.areaCode} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="py-2 px-3 font-mono font-medium">{m.areaCode}</td>
                          <td className="py-2 px-3">{m.city}</td>
                          <td className="py-2 px-3 text-muted-foreground">{m.state}</td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-[10px]">{m.market}</Badge>
                          </td>
                          <td className="py-2 px-3">
                            {hasNumber ? (
                              <span className="flex items-center gap-1 text-success"><CheckCircle className="h-3 w-3" /> Covered</span>
                            ) : (
                              <span className="text-muted-foreground">No direct number</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Call Log ── */}
        <TabsContent value="log" className="mt-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <PhoneOutgoing className="h-4 w-4 text-primary" /> Outbound Call Log
              </CardTitle>
              <CardDescription className="text-xs">
                All outbound calls with local presence number selection tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Time</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Caller</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Target</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Outbound #</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Match</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((l) => (
                      <tr key={l.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{l.timestamp}</td>
                        <td className="py-2 px-3 font-medium">{l.userName}</td>
                        <td className="py-2 px-3">
                          <div>{l.targetName}</div>
                          <div className="text-muted-foreground">{l.targetNumber}</div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-mono">{l.selectedOutboundNumber}</div>
                          <div className="text-muted-foreground">{l.selectedOutboundCity}</div>
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={`text-[10px] ${MATCH_STYLES[l.matchType]?.color || ""}`}>
                            {MATCH_STYLES[l.matchType]?.label || l.matchType}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={`text-[10px] ${RESULT_STYLES[l.result]?.color || ""}`}>
                            {RESULT_STYLES[l.result]?.label || l.result}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={addDialog || !!editDialog} onOpenChange={(v) => { if (!v) { setAddDialog(false); setEditDialog(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Phone className="h-4 w-4" /> {editDialog ? "Edit Number" : "Add Outbound Number"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone Number</Label>
                <Input
                  value={draft.phoneNumber || ""}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    const cleaned = formatted.replace(/\D/g, "");
                    const ac = cleaned.length >= 3 ? cleaned.slice(0, 3) : "";
                    setDraft((d) => ({ ...d, phoneNumber: formatted, areaCode: ac }));
                  }}
                  placeholder="(813) 555-0100"
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Area Code</Label>
                <Input value={draft.areaCode || ""} onChange={(e) => setDraft((d) => ({ ...d, areaCode: e.target.value }))} placeholder="813" className="h-8 text-xs mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={draft.city || ""} onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} placeholder="Tampa" className="h-8 text-xs mt-1" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={draft.state || ""} onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value }))} placeholder="FL" className="h-8 text-xs mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Provider Identifier</Label>
              <Input value={draft.providerId || ""} onChange={(e) => setDraft((d) => ({ ...d, providerId: e.target.value }))} placeholder="twilio-813-001" className="h-8 text-xs mt-1" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={draft.active !== false} onCheckedChange={(v) => setDraft((d) => ({ ...d, active: v }))} />
                <Label className="text-xs">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setAddDialog(false); setEditDialog(null); }}>Cancel</Button>
            <Button size="sm" onClick={editDialog ? handleSaveEdit : handleSaveNew}>
              {editDialog ? "Save Changes" : "Add Number"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
